import { useModalContentToggler } from '@components'

import { Button, ButtonsGroup, useModal } from '@billy/design-system'
import { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'

import { useInvalidateInvoices } from '@features/invoices/hooks/useInvalidateInvoices'
import { InvoiceType } from '@features/invoicesLegacy/types/invoiceType'
import { BankMatchUiFlow, UiSource } from '@features/reconciliation/enums'
import { useReconcileInvoice } from '@features/reconciliation/hooks/useReconcileInvoice'

import { QueryKeys } from '../../../../enums/queryKeys'
import { useSegment } from '../../../../hooks'
import { isSubjectMatchButtonDisabled } from '../../../bills/utils/isSubjectMatchButtonDisabled'
import {
  useManualPayment,
  useMatch,
  useMultiplePayableSubjects,
  usePaymentModal,
} from '../../../paymentReconciliationModal/contexts'
import { useCreateSubjectReconciliationSuggestion } from '../../../paymentReconciliationModal/hooks'
import { getTypeFromPayableSubject } from '../../../paymentReconciliationModal/utils'
import { SubjectReconciliationModalStateId } from '../../enums/SubjectReconciliationModalStateId'
import { getDifferenceType } from '../BankLinesModalFooter/utils'
import { MultipleSubjectsButton } from '../MultipleSubjectsButton'

export const InvoiceMatchModalFooter = (): ReactElement => {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const { track } = useSegment()
  const { selectedDifferenceType, modalId, isCrossCurrency } = usePaymentModal()
  const { initialSubject, selectedSubjectsAmountsSum, isCashBasis } = useMultiplePayableSubjects()
  const { crossCurrencyPaymentAmount } = useManualPayment()
  const { hasMatchDifference, matchSuggestion } = useMatch()
  const { setActiveState } = useModalContentToggler()
  const { isProcessing } = useCreateSubjectReconciliationSuggestion()
  const { invalidateExternalInvoice } = useInvalidateInvoices()

  const { close: closeMatchModal } = useModal(modalId)

  const differenceType = useMemo(
    () => getDifferenceType(selectedSubjectsAmountsSum, crossCurrencyPaymentAmount, selectedDifferenceType),
    [selectedSubjectsAmountsSum, crossCurrencyPaymentAmount, selectedDifferenceType],
  )

  const initialSubjectId = initialSubject?.id
  const subjectType = getTypeFromPayableSubject(initialSubject)
  const invoiceType = subjectType === InvoiceType.ExternalInvoice ? InvoiceType.ExternalInvoice : InvoiceType.Invoice

  const onSuccess = async () => {
    closeMatchModal()
    track('xxx - expenses - Pay and reconcile modal - register payment clicked')
    if (initialSubjectId) {
      await queryClient.cancelQueries({ queryKey: [QueryKeys.InvoiceReconciliationMatches, initialSubjectId] })
      queryClient.setQueryData([QueryKeys.InvoiceReconciliationMatches, initialSubjectId], undefined)
    }
    queryClient.invalidateQueries([QueryKeys.Invoices])
    invalidateExternalInvoice(initialSubjectId)
  }

  const { reconcileSubject, isProcessing: isProcessingReconcile } = useReconcileInvoice({
    selectedDifferenceType,
    onSuccess,
    invoiceId: initialSubjectId,
    organizationId: initialSubject?.organizationId,
    crossCurrencyPaymentAmount,
    bankLineId: matchSuggestion?.id,
    differenceType,
    invoiceType,
    isAutoMatch: true,
    uiSource: UiSource.PayAndRecon,
    uiFlow: BankMatchUiFlow.Matched,
  })

  const isEnterPaymentButtonDisabled = useMemo(() => {
    return isSubjectMatchButtonDisabled({
      isProcessing,
      isCrossCurrency,
      crossCurrencyPaymentAmount,
      hasMatchDifference,
      selectedDifferenceType,
    })
  }, [isProcessing, isCrossCurrency, crossCurrencyPaymentAmount, hasMatchDifference, selectedDifferenceType])

  const handleEnterPayment = useCallback(() => {
    reconcileSubject()
  }, [reconcileSubject])

  const handleRegisterMultipleInvoicesButton = useCallback(() => {
    track('xxx - expenses - Pay and reconcile modal - register mutliple invoices clicked')

    setActiveState(SubjectReconciliationModalStateId.MultipleSubjects)
  }, [setActiveState, track])

  return (
    <ButtonsGroup>
      {!isCashBasis && (
        <MultipleSubjectsButton onClick={handleRegisterMultipleInvoicesButton} payableSubjectType={invoiceType} />
      )}
      <Button onClick={handleEnterPayment} loading={isProcessingReconcile} disabled={isEnterPaymentButtonDisabled}>
        {t('bill.payment_reconcile.button.enter_payment_and_reconcile')}
      </Button>
    </ButtonsGroup>
  )
}
